import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useState, useRef, useEffect } from "react";
import { checkSession } from "../auth/authService";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import ThankYou from "../components/checkout/ThankYou";
import Logo from '../assets/MALogo.svg'
import { useNavigate } from 'react-router-dom';
import PageLoader from "../components/loaders/PageLoader";
import SubscriptionPageError from "../components/errorPages/SubscriptionPageError";
import ToastNotification from "../components/ToastNotification";

const Checkout = () => {
    const [user, setUser] = useState(null);
    const [subType, setSubType] = useState(null); // Subscription type: 1MF, 2MF, 3MF
    const [amount, setAmount] = useState(null);
    const [invoiceId, setInvoiceId] = useState(null);
    const [expirationDate, setExpirationDate] = useState(null);
    const [isCollapsed, setIsCollaped] = useState(true);
    const [activeContainer, setActiveContainer] = useState("purchase");
    const [termsChecked, setTermsChecked] = useState(false); // State for Terms checkbox
    const [consentChecked, setConsentChecked] = useState(false); // State for Consent checkbox
    const [paymentSuccess, setPaymentSuccess] = useState(false); // State to track if payment was successful
    const [isLoading, setIsLoading] = useState(true); // State to track loading status
    const [authResponse, setAuthResponse] = useState(null);
    const [authDescription, setAuthDescription] = useState(null);
    const [serverMessage, setServerMessage] = useState(''); // State to hold server message
    const [paymentErrorLocation, setPaymentErrorLocation] = useState(null);

    const isPayPalButtonDisabled = !termsChecked || !consentChecked; // Disable PayPal buttons if either checkbox is unchecked
    const navigate = useNavigate();

    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    const getUserData = async () => {
        try {
            const response = await fetch(`${apiUrl}/user/getuser/`, {
                method: "GET",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();

            if (data.success) {
                
                //check if email is verified, else redirect to verify page
                if (!data.data.email_verified) {
                    setAuthDescription("Please verify your email before subscribing. This helps us ensure the security of your account.");
                    setAuthResponse(406);
                    return;
                }
                
                setUser(data.data);
                setIsLoading(false);
            } 

        } catch (error) {
            console.error("Error fetching user's information:", error);
            setAuthDescription("There was an error fetching your user data. Please try again later.");
            setAuthResponse(500);
        }
    }

    const subscribe = async (subscriptionId) => {

        //generate current for the subscription in the format of YYYY-MM-DD
        const currentDate = new Date();

        // Add id to the item object on the backend
        const item = {
            id: user.userId,
            email: user.email,
            createdDate: currentDate.toISOString().split('T')[0],
            expirationDate: expirationDate,
            paymentMethod: 'paypal', // Change this logic if you expand to multiple payment methods like card, stripe, venmo, etc.
            subscriptionId: subscriptionId,
            subscriptionStatus: "ACTIVE",
            subscriptionType: subType,
        }

        try {
            const response = await fetch(`${apiUrl}/subscription/create`, {
                method: "POST",
                // credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(item)
            });

            const data = await response.json();

            if (response.status === 200) {
                // Subscription created successfully
            } else {
            setPaymentErrorLocation("subscribe");
            throw new Error("Subscription creation failed");
            }

        } catch (error) {
        setPaymentErrorLocation("subscribe");
        throw new Error("Error creating subscription");
        }
    }

    const handleOrderCreate = async (data, actions) => {
        // Create a one-time payment order

        return actions.order.create({
            application_context: {
                shipping_preference: "NO_SHIPPING", // No shipping address needed for digital goods
            },
            purchase_units: [
                {
                    amount: {
                        value: amount, // Dynamically set the amount based on the selected plan
                    },
                    description: `Melee Academy - ${subType.split('')[0]} Month Access`, // Description based on the selected plan
                    custom_id: `subtype=${subType}&userId=${user.userId}`, // Custom data to identify user and subscription type
                },
            ],
        });
    }

    const handleOnApprove = async (data, actions) => {
        try {
            const order = await actions.order.capture();
            const captureId = order.purchase_units[0].payments.captures[0].id;
            const orderId = data.orderID;

            setInvoiceId(orderId); // Store orderID as invoiceId for Thank You page
            await createInvoice(orderId, captureId);
            await subscribe(orderId); 

            setPaymentSuccess(true);

        } catch (error) {
            // Handle errors conditionally based on where they occurred
            console.error("Error during payment processing:", error);
            
            if (paymentErrorLocation === "createInvoice") {
                setAuthDescription("Your payment was successful, and we're preparing your invoice and activating your subscription. Our system will automatically retry to complete the process. You can check your account page for updates. If your subscription isn't activated within 72 hours, please contact support.");
                setAuthResponse(500);
            } else if (paymentErrorLocation === "subscribe") {
                setAuthDescription("Your payment was successful, and your invoice has been saved to your account. We're working to activate your subscription access. Our system will automatically retry to complete the process. You can check your account page for updates. If your subscription isn't activated within 72 hours, please contact support for assistance.");
                setAuthResponse(500);
            } else {
                setAuthDescription("We encountered a temporary issue while processing your subscription. Don't worry—our system will automatically retry and finalize your subscription and invoice. You can check your account page for updates. If your subscription isn't activated within 72 hours, please contact support for further assistance.");
                setAuthResponse(500);
            }
        }
    };

    const sendInvoiceEmail = async (orderId) => {

        const plans = {
            "1MF": "1 Month Access",
            "2MF": "2 Month Access",
            "3MF": "3 Month Access",
        };

        const emailDetails = {
            username: user.username,
            email: user.email,
            invoiceId: orderId,
            courseName: "Foundation Course",
            planName: plans[subType],
            startDate: new Date().toISOString().split('T')[0],
            endDate: expirationDate,
            price: amount + " USD",
        }

        try {
            const response = await fetch(`${apiUrl}/subscription/send-invoice-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(emailDetails)
            });

            const data = await response.json();

            if (data.success) {
                // Invoice email sent successfully
            }
            else {
            setPaymentErrorLocation("sendInvoiceEmail");
            throw new Error("Invoice email sending failed");
            }
        } catch (error) {
            setPaymentErrorLocation("sendInvoiceEmail");
            throw new Error("Error sending invoice email");
        }
    }

    const createInvoice = async (invoiceId, captureId) => {
        const invoiceDetails = {
            invoiceId : invoiceId, // Use the subscriptionId or orderId as the invoiceId
            captureId: captureId,
            createdDate: new Date().toISOString().split('T')[0],
            expirationDate: expirationDate,
            paymentAmount: amount,
            paymentCurrency: "USD",
            paymentMethod: "paypal",
            paymentStatus: "PAID",
            purchaseType: subType,
            userId: user.userId,
        }

        try {
            const response = await fetch(`${apiUrl}/subscription/create-invoice`, {
                method: "POST",
                // credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(invoiceDetails)
            });

            const data = await response.json();

            if (data.success) {
                // Invoice created successfully
            }
            else {
                setPaymentErrorLocation("createInvoice");
                throw new Error("Invoice creation failed");
            }
        } catch (error) {
            setPaymentErrorLocation("createInvoice");
            throw new Error("Error creating invoice");
        }
}

    const getUserSubscriptionData = async () => {
        try {
            const response = await fetch(`${apiUrl}/subscription/existing-subscription`, {
                method: "GET",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();

            if (data.success && data.subscription) {
                return data.subscription;
            }

            // If no subscription is found, return false
            return null;

        } catch (error) {
            console.error("Error fetching subscription data:", error);
            setAuthDescription("There was an error fetching your subscription data. Please try again later.");
            setAuthResponse(500);
            // navigate("/");
        }
    }

    const authorizeUser = async () => {

        const sessionValid = await verifySession();

        // If session is not valid, stop further execution which should show the 401 error
        if (!sessionValid) {
            return;
        }

        const subscriptionData = await getUserSubscriptionData();

        // If no subscription data exists, skip further checks
        if (!subscriptionData) {
            await getUserData();
            setIsLoading(false);
            setAuthResponse(200);
            return;
        }

        //check expiration date of subscription
        const currentDate = new Date();
        const [year, month, day] = subscriptionData.expirationDate.split('-');
        let isExpired = new Date(year, month - 1, day) < currentDate;


        // Depending what subscription data you get, you have to manage what the user can do with the subscription 
        if (subscriptionData.subscriptionStatus === "ACTIVE" && !isExpired) {
            setAuthDescription("You already have an active subscription. If you wish to extend your subscription period, please wait until it expires and try again.");
            setAuthResponse(405);
            return;
            // navigate("/");
        }

        setIsLoading(false);
        setAuthResponse(200);
        await getUserData();
    }

    const verifySession = async () => {
        const isLoggedIn = await checkSession();
        // add a quick delay to show the loader
        await new Promise(resolve => setTimeout(resolve, 3000)); // 3 seconds delay
        setIsLoading(false);
        if (!isLoggedIn) {
            // alert("You are not logged in. Please log in to access this page.");
            // navigate("/login");
            setAuthDescription("You are not logged in. Please log in to access the checkout page.");
            setAuthResponse(401);
            return false;
        }
        return true;
    } 

    const toggleCollapse = () => {
        setIsCollaped(!isCollapsed);
    }

    const toggleContainer = (container) => {
        setActiveContainer(container);
    }

        // Get last param from URL to prefill the subscription type (example: /pricing/subscribe/one-month)
    useEffect(() => {
        const pathParts = window.location.pathname.split('/');
        const tier = pathParts[pathParts.length - 1]; // Get the last part of the URL

        // Use this for production but use 1 day expiry for testing
        const oneMonthExpiry = new Date();
        oneMonthExpiry.setMonth(oneMonthExpiry.getMonth() + 1);
        const twoMonthExpiry = new Date();
        twoMonthExpiry.setMonth(twoMonthExpiry.getMonth() + 2);
        const threeMonthExpiry = new Date();
        threeMonthExpiry.setMonth(threeMonthExpiry.getMonth() + 3);

        // One day expiry for testing
        // const oneMonthExpiry = new Date();
        // oneMonthExpiry.setDate(oneMonthExpiry.getDate() + 1);
        // const twoMonthExpiry = new Date();
        // twoMonthExpiry.setDate(twoMonthExpiry.getDate() + 2);
        // const threeMonthExpiry = new Date();
        // threeMonthExpiry.setDate(threeMonthExpiry.getDate() + 3);
        
        //no basic or pro tiers. Changed to one-month, two-month, etc. for the new pricing model
        if (tier === "one-month") {
                setSubType("1MF");
                setAmount("25.00");
                setExpirationDate(oneMonthExpiry.toISOString().split('T')[0]);
        } else if (tier === "two-months") {
                setSubType("2MF");
                setAmount("40.00");
                setExpirationDate(twoMonthExpiry.toISOString().split('T')[0]);
        } else if (tier === "three-months") {
                setSubType("3MF");
                setAmount("60.00");
                setExpirationDate(threeMonthExpiry.toISOString().split('T')[0]);
        } else {
            // alert("REQUIRES PAGE DOES NOT EXIST ERROR PAGE WITH 404");
            // navigate("/pricing");
            setAuthDescription("The subscription plan you are trying to access does not exist.");
            setAuthResponse(404);
            setIsLoading(false);
            return;
        }

        authorizeUser();

    }, []);

    return (
        
        authResponse === 401 || authResponse === 404 || authResponse === 405 || authResponse === 406 || authResponse === 500 ? (
            <SubscriptionPageError status={authResponse} message={authDescription} />
        // user !== null ? (
        ) : authResponse === 200 ? ( 
            <PayPalScriptProvider
                options={{
                    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                    "vault": true,
                    "intent": "capture",
                }}
            >
                <div className="wrapper">
                    <div className="d-flex justify-content-center" style={{paddingTop: "40px", cursor: "pointer"}} onClick={() => navigate('/')}>
                        <img src={Logo} alt="Melee Academy Logo" style={{width: "50px", marginRight: "12px"}}/>
                        <h2 style={{fontWeight: 600}}>Melee Academy</h2>
                    </div>

                    <div className='background-grid d-flex flex-column justify-content-center align-items-center' style={{minHeight: "80vh"}}>
                        {paymentSuccess ? (
                            <ThankYou subType={subType} amount={amount} expirationDate={expirationDate} invoiceId={invoiceId} />
                        ) : (
                            // <div className="container mt-5">
                                <div className="d-flex flex-column align-items-center mt-5">

                                    {activeContainer === "checkout" && (
                                        <div className="rounded p-3 mb-3 col-10 col-md-6" style={{ backgroundColor: "#0B0E1E", border: "1px solid #E5E5E5" }}>
                                            <div className="d-flex col justify-content-between align-items-center">
                                                <p className="m-0"><b>Purchase Details</b></p>
                                                <p
                                                    className="m-0"
                                                    style={{ color: "#FFCC19", fontWeight: 600, cursor: "pointer" }}
                                                    onClick={() => toggleContainer("purchase")} // Switch back to purchase container
                                                >
                                                    Edit
                                                </p>
                                            </div>
                                        </div>
                                    )}    
                                    {activeContainer === "purchase" && (
                                        <div className='rounded p-5 col-10 col-md-7' style={{backgroundColor: "#0B0E1E", border: "1px solid #E5E5E5"}}>
                                                <div className="d-flex justify-content-between mb-2">
                                                    {subType === "1MF" && <h4>1 Month Access</h4>}
                                                    {subType === "2MF" && <h4>2 Month Access</h4>}
                                                    {subType === "3MF" && <h4>3 Month Access</h4>}
                                                    <p>${amount} USD</p>
                                                </div>
                                                <p>This will be billed once and you will have access to the content until the expiration date below.</p>
                                                {/* <p><b>Trial Period:</b> ONE DAY TEST FOR NOW</p> */}
                                                <hr></hr>
                                                <div>
                                                    <p className="m-0"><b>Expiration date:</b> {expirationDate}</p>
                                                    <p><b>Payment method:</b> PayPal</p>
                                                </div>
                                                <div>
                                                    <div className="d-flex flex-row align-items-start" style={{cursor: "pointer"}} data-bs-toggle="collapse" href="#includedCollapse" role="button" aria-expanded="false" aria-controls="includedCollapse">
                                                        <FontAwesomeIcon
                                                            className="me-2 mt-1"
                                                            icon={faChevronDown}
                                                            style={{ cursor: "pointer", color: "#AEAFBA", cursor: "pointer" }}
                                                            onClick={() => toggleCollapse()}
                                                        />
                                                        <h5>
                                                            What's included
                                                        </h5>
                                                    </div>
                                                    <div className="m-0">
                                                        {!isCollapsed && ( // Render content conditionally based on collapse state
                                                            <ul className="ms-3">
                                                                <li>Foundation course</li>
                                                                <li>4 sections</li>
                                                                <li>23 lessons</li>
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                                <button className='rounded w-100 mt-3' style={{backgroundColor: "#FFCC19", height: "45px", fontWeight: 600, border: "none", color: "black"}} onClick={() => toggleContainer("checkout")}>Continue to Payment</button>
                                                <p className='text-center mb-0' style={{color: '#AEAFBA', fontWeight: 400, fontSize: "14px", marginTop: "20px"}}>Want longer access? <a href='/pricing' style={{color: 'white', fontWeight: 600, textDecoration: 'none'}}>Return to plans.</a></p>
                                            </div>
                                    )}
                                        

                                    {activeContainer === "purchase" && (
                                        <div className="rounded p-3 mb-3 mt-3 col-10 col-md-7" style={{ backgroundColor: "#0B0E1E", border: "1px solid #E5E5E5" }}>
                                            <div className="d-flex col justify-content-between align-items-center">
                                                <p className="m-0"><b>Checkout</b></p>
                                            </div>
                                        </div>
                                    )}
                                    {activeContainer === "checkout" && (    
                                        <div className='rounded p-5 mb-4 col-10 col-md-6' style={{backgroundColor: "#0B0E1E", border: "1px solid #E5E5E5"}}>
                                            <h4 className="mb-3 text-center">Order Summary</h4>
                                            <hr></hr>
                                            <div className="mb-2 rounded p-3" style={{backgroundColor: "#1C1F26"}}>
                                                <p className="m-0"><b>Subscription Amount:</b> ${amount}</p>
                                                <p className="m-0"><b>Processing Fee:</b> $0.00</p>
                                                <p className="m-0"><b>Sales Tax:</b> $0.00</p>
                                                <p className="m-0"><b>Total (USD):</b> ${amount}</p>
                                            </div>
                                            {/* Checkboxes for terms and consent */}
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="terms"
                                                    checked={termsChecked}
                                                    onChange={(e) => setTermsChecked(e.target.checked)} // Update state when checkbox is clicked
                                                />
                                                <label className="form-check-label" htmlFor="terms">
                                                    I understand this is a one-time payment and access will remain active until the selected plan expires.
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="consent"
                                                    checked={consentChecked}
                                                    onChange={(e) => setConsentChecked(e.target.checked)} // Update state when checkbox is clicked
                                                />
                                                <label className="form-check-label" htmlFor="consent">
                                                    I understand Melee Academy is currently in beta. Account information is used only to manage access, subscriptions, and platform functionality.
                                                </label>
                                            </div>
                                            <hr></hr>
                                            <div>
                                                <PayPalButtons
                                                    style={{
                                                        shape: "rect",
                                                        layout: "vertical",
                                                        color: "gold",
                                                        label: "paypal",
                                                    }}
                                                    disabled={isPayPalButtonDisabled} // Disable button if terms are not agreed
                                                    createOrder={handleOrderCreate}
                                                    onApprove={handleOnApprove}
                                                    onCancel={(data) => {
                                                        // Handle payment cancellation
                                                        setServerMessage({success: false, message: "Payment was cancelled. If this was a mistake, please try again."});
                                                    }}
                                                    onError={(err) => {
                                                        // Handle errors during the payment process
                                                        console.error("PayPal Button Error:", err);
                                                        setServerMessage({success: false, message: "There was an error processing your payment. Please try again."});
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            // </div>
                        )}
                    </div>
                </div>
                <ToastNotification
                    message={serverMessage}
                    setServerMessage={setServerMessage}
                />
            </PayPalScriptProvider>
        ) : (
            <PageLoader isLoading={isLoading} />
        )
        
    );
}

export default Checkout
